
import React, { useState, useEffect } from 'react';
import { AnalysisResult } from '../types';
import Speedometer from './Speedometer';
import DoctIABanner from './DoctIABanner';
import AdvertisingModal from './AdvertisingModal';
import { CheckCircle2, XCircle, Copy, Check, Sparkles, Scale, FileText, Share2, Linkedin, Instagram, MessageCircle } from 'lucide-react';
import { getModalConfig } from '../services/adminService';
import { safeGetItem, safeSetItem } from '../services/storageService';

interface AnalysisViewProps {
  result: AnalysisResult;
  prompt: string;
  onReset: () => void;
}

const AnalysisView: React.FC<AnalysisViewProps> = ({ result, prompt, onReset }) => {
  const [copied, setCopied] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<any>(null);

  useEffect(() => {
    const checkAndShowModal = async () => {
      const config = await getModalConfig();
      setModalConfig(config);

      if (!config.enabled) {
        return;
      }

      // Check if we should show the modal based on frequency
      const now = Date.now();
      const lastShown = safeGetItem('last_modal_shown', '0');
      const lastShownTime = lastShown ? parseInt(lastShown) : 0;

      let shouldShow = false;

      switch (config.frequency) {
        case 'always':
          shouldShow = true;
          break;
        case 'once_per_session':
          // Session-based: show only once per browser session
          const sessionShown = sessionStorage.getItem('modal_shown_this_session');
          shouldShow = !sessionShown;
          break;
        case 'once_per_day':
          const dayInMs = 24 * 60 * 60 * 1000;
          shouldShow = (now - lastShownTime) > dayInMs;
          break;
        case 'once_per_week':
          const weekInMs = 7 * 24 * 60 * 60 * 1000;
          shouldShow = (now - lastShownTime) > weekInMs;
          break;
        case 'every_2_analyses':
        case 'every_3_analyses':
        case 'every_5_analyses':
          // Analysis count-based: show every N analyses
          const analysisCount = parseInt(safeGetItem('modal_analysis_count', '0')) || 0;
          const newCount = analysisCount + 1;
          safeSetItem('modal_analysis_count', newCount.toString());
          
          let showEveryN = 2;
          if (config.frequency === 'every_3_analyses') showEveryN = 3;
          else if (config.frequency === 'every_5_analyses') showEveryN = 5;
          
          shouldShow = (newCount % showEveryN) === 0;
          break;
      }

      if (shouldShow) {
        setShowModal(true);
      }
    };

    checkAndShowModal();
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    const now = Date.now();
    safeSetItem('last_modal_shown', now.toString());
    sessionStorage.setItem('modal_shown_this_session', 'true');
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(result.optimizedPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform: string) => {
    const shareUrl = "https://lps.inteligencialegal.com.ar";
    const shareMessage = `He creado un prompt optimizado para tareas legales. Mirá: "${result.optimizedPrompt}"\n\nOptimizá vos también tus prompts legales con LegalPromptStudio: ${shareUrl}`;
    const encodedText = encodeURIComponent(shareMessage);

    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodedText}`, '_blank');
        break;
      case 'instagram':
        navigator.clipboard.writeText(shareMessage);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
        break;
      case 'copy':
        navigator.clipboard.writeText(shareMessage);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
        break;
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700">
      
      {/* Advertising Modal */}
      {modalConfig && (
        <AdvertisingModal 
          isOpen={showModal}
          config={modalConfig}
          onClose={handleCloseModal}
        />
      )}

      {/* Original Prompt Card (Collapsible/Static) */}
      <div className="mb-8 bg-zinc-900/30 border border-zinc-800 rounded-2xl p-6 relative overflow-hidden">
         <div className="flex items-center gap-2 mb-3 text-zinc-400">
            <FileText className="w-4 h-4" />
            <h3 className="text-sm font-bold uppercase tracking-wider">Prompt Original</h3>
         </div>
         <p className="text-gray-300 italic text-sm leading-relaxed opacity-80">
            "{prompt}"
         </p>
      </div>

      {/* Top Section: Score & Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        {/* Score Card */}
        <div className="col-span-1 bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-3xl flex items-center justify-center shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-50" />
          <Speedometer score={result.score} />
        </div>

        {/* Explanation Card */}
        <div className="col-span-1 md:col-span-2 bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent opacity-50" />
            <h2 className="text-2xl font-bold text-gray-100 mb-4 flex items-center gap-2">
                <Scale className="text-purple-400" />
                Diagnóstico
            </h2>
            <p className="text-gray-400 leading-relaxed text-lg">
                {result.explanation}
            </p>
        </div>
      </div>

      {/* Checklist Section */}
      <div className="mb-12">
        <h3 className="text-xl font-bold text-gray-200 mb-6 flex items-center gap-2 px-2">
          <span className="w-1 h-6 bg-cyan-500 rounded-full inline-block"></span>
          Análisis de Cumplimiento
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {result.checks.map((check, idx) => (
            <div 
              key={idx} 
              className={`p-5 rounded-2xl border transition-all duration-300 hover:scale-[1.02] ${
                check.status === 'pass' 
                  ? 'bg-emerald-950/20 border-emerald-900/50 hover:border-emerald-500/50' 
                  : 'bg-red-950/20 border-red-900/50 hover:border-red-500/50'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <span className={`font-bold text-sm uppercase tracking-wider ${
                  check.status === 'pass' ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {check.label}
                </span>
                {check.status === 'pass' ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                {check.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Optimized Prompt Section */}
      <div className="bg-gradient-to-b from-zinc-900 to-black border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl relative">
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
        
        <div className="p-6 border-b border-zinc-800 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-zinc-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/10 rounded-lg">
                <Sparkles className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-100">Prompt Optimizado</h3>
              <p className="text-sm text-cyan-500/80 font-medium">Formato Legal Sugerido</p>
            </div>
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white transition-all font-medium border border-zinc-700 hover:border-gray-500 active:scale-95"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copiado' : 'Copiar Prompt'}
          </button>
        </div>

        <div className="p-8 relative">
            <div className="absolute top-0 left-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none"></div>
            <pre className="whitespace-pre-wrap font-mono text-sm md:text-base text-gray-300 leading-relaxed bg-black/50 p-6 rounded-xl border border-zinc-800/50">
                {result.optimizedPrompt}
            </pre>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-zinc-800/50">
              <span className="text-zinc-400 text-sm font-medium">Compartir prompt:</span>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleShare('whatsapp')} 
                  className="p-2 rounded-lg bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] transition-colors" 
                  title="Compartir en WhatsApp"
                >
                  <MessageCircle className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleShare('linkedin')} 
                  className="p-2 rounded-lg bg-[#0077b5]/10 hover:bg-[#0077b5]/20 text-[#0077b5] transition-colors" 
                  title="Compartir en LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleShare('instagram')} 
                  className="p-2 rounded-lg bg-[#E4405F]/10 hover:bg-[#E4405F]/20 text-[#E4405F] transition-colors" 
                  title="Copiar para Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleShare('copy')} 
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors text-sm font-medium"
                >
                  {shareCopied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                  {shareCopied ? 'Copiado' : 'Copiar y compartir'}
                </button>
              </div>
            </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-12 text-center pb-8">
        <button
          onClick={onReset}
          className="bg-white hover:bg-gray-200 text-black font-bold py-3 px-8 rounded-xl transition-all shadow-[0_0_20px_rgba(255,255,255,0.15)] hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] flex items-center gap-2 mx-auto active:scale-95"
        >
          <Sparkles className="w-4 h-4" />
          Analizar otro prompt
        </button>
      </div>

      <DoctIABanner />
    </div>
  );
};

export default AnalysisView;
